import { useEffect, useState, type ComponentType } from "react"
import { Box, Tooltip } from "@mui/material"
import { AtSign, FileText, Mail, Users } from "lucide-react"

import { CAMPAIGN_SECTIONS } from "./sections"

const ICONS: Record<string, ComponentType<{ size?: number }>> = {
  "campaign-section-from": AtSign,
  "campaign-section-to": Users,
  "campaign-section-body": Mail,
  "campaign-section-attachments": FileText,
}

/**
 * A sticky vertical ICON rail (one icon per editor section) — labels live in
 * tooltips so the rail stays narrow. Click an icon to smooth-scroll to that
 * section; the icon for the section currently in view highlights. Hidden below
 * `lg` (the sections just stack + scroll on narrow screens).
 */
export default function SectionNav() {
  const [activeId, setActiveId] = useState<string>(CAMPAIGN_SECTIONS[0]?.id ?? "")

  useEffect(() => {
    const compute = () => {
      const offset = 160 // the "current section" line, px from the viewport top
      let current = CAMPAIGN_SECTIONS[0]?.id ?? ""
      for (const s of CAMPAIGN_SECTIONS) {
        const el = document.getElementById(s.id)
        if (el && el.getBoundingClientRect().top - offset <= 0) current = s.id
      }
      // At (near) the bottom, force the LAST section active — a short final
      // section can't push its top above the offset line, so it would never
      // light up otherwise (the "Attachments never activates" bug).
      const doc = document.scrollingElement || document.documentElement
      if (doc && doc.scrollHeight - doc.scrollTop - doc.clientHeight < 8) {
        current = CAMPAIGN_SECTIONS[CAMPAIGN_SECTIONS.length - 1]?.id ?? current
      }
      setActiveId(current)
    }
    compute()
    // capture=true catches scroll from whatever element actually scrolls (the
    // page may scroll on a content div, not the window).
    window.addEventListener("scroll", compute, true)
    window.addEventListener("resize", compute)
    return () => {
      window.removeEventListener("scroll", compute, true)
      window.removeEventListener("resize", compute)
    }
  }, [])

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
    setActiveId(id)
  }

  return (
    <Box
      component="nav"
      aria-label="Campaign sections"
      sx={{
        display: { xs: "none", lg: "flex" },
        position: "sticky",
        top: 124,
        flexDirection: "column",
        gap: 1.5,
        pt: 1,
      }}
    >
      {CAMPAIGN_SECTIONS.map((s) => {
        const Icon = ICONS[s.id] ?? Mail
        const active = s.id === activeId
        return (
          <Tooltip key={s.id} title={s.label} placement="right" arrow>
            <Box
              role="button"
              tabIndex={0}
              aria-label={`Go to ${s.label}`}
              aria-current={active ? "true" : undefined}
              onClick={() => scrollTo(s.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  scrollTo(s.id)
                }
              }}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                outline: "none",
                transition: "all 0.2s",
                border: 1,
                bgcolor: active ? "primary.main" : "action.hover",
                color: active ? "primary.contrastText" : "text.secondary",
                borderColor: active ? "primary.main" : "divider",
                "&:hover": {
                  bgcolor: active ? "primary.dark" : "action.selected",
                  color: active ? "primary.contrastText" : "text.primary",
                },
                "&:focus-visible": { boxShadow: (t) => `0 0 0 3px ${t.palette.primary.main}55` },
              }}
            >
              <Icon size={18} />
            </Box>
          </Tooltip>
        )
      })}
    </Box>
  )
}
